import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { t } from 'i18next';
import { DateTime } from 'luxon';
import { cloneDeep, differenceWith, get, isEqual, omit, orderBy } from 'lodash';

import { Autocomplete, Box, Chip, TextField } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import DeleteIcon from '@mui/icons-material/Delete';

import { ReactComponent as Export } from 'assets/icons/Material/fileDownload.svg';

import {
  ColumnProps,
  ConfirmDialog,
  DeleteDialog,
  Page,
  PageHeaderAction,
  Table,
  showNotification,
  useDialogState,
  usePanelState,
  useTableQueryParams,
} from 'components';
import { NotificationType, PermissionGroupModel, UserModel } from 'models';
import { useDeleteUser, useEditUser, useExportUsers, useUserList } from 'features/users';
import { usePermissionGroupListSearch } from 'features/permissionGroups';
import { stringify } from 'utils/base64';
import { useCanEdit } from 'utils/auth';

type TRecord = Record<string, any>;

const styles = {
  chips: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 0.5,
  },
  inactiveChip: {
    opacity: 0.5,
  },
};

interface PermissionGroupsCellProps {
  userData: UserModel;
  visible: boolean;
  activeGroups: PermissionGroupModel[];
  onChange: (groups: PermissionGroupModel[]) => void;
}

const PermissionGroupsCell: FC<PermissionGroupsCellProps> = ({
  userData,
  visible,
  activeGroups,
  onChange,
}) => {
  const assignedGroups = useMemo(() => userData.permissionGroups || [], [userData.permissionGroups]);

  const options = useMemo(() => {
    const merged = [...activeGroups];
    assignedGroups.forEach((group) => {
      if (!group.isActive && !merged.some((option) => option.id === group.id)) {
        merged.push(group);
      }
    });
    return merged;
  }, [activeGroups, assignedGroups]);

  if (!visible) {
    return (
      <Box sx={styles.chips}>
        {assignedGroups.map((group) => (
          <Chip
            key={group.id}
            label={group.name}
            size="small"
            sx={!group.isActive ? styles.inactiveChip : undefined}
          />
        ))}
      </Box>
    );
  }

  return (
    <Autocomplete
      multiple
      size="small"
      fullWidth
      options={options}
      value={assignedGroups}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => option.name}
      getOptionDisabled={(option) => !option.isActive}
      onChange={(_event, newValue) => onChange(newValue)}
      onClick={(event) => event.stopPropagation()}
      noOptionsText={<Trans i18nKey={'COMMON.SELECT_FROM_LIST'} />}
      renderOption={(props, option) => (
        <li {...props} key={option.id} style={!option.isActive ? { opacity: 0.5 } : undefined}>
          {option.name}
        </li>
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const tagProps = getTagProps({ index });
          return (
            <Chip
              {...tagProps}
              key={option.id}
              label={option.name}
              size="small"
              sx={!option.isActive ? styles.inactiveChip : undefined}
            />
          );
        })
      }
      renderInput={(params) => <TextField {...params} placeholder={t('COMMON.CHOOSE')} />}
    />
  );
};

const OpemUsers: FC = () => {
  const {
    onPageChange,
    onRowsPerPageChange,
    onSort,
    onLoadQuery,
    params,
    apiParams,
    exportParams,
    onTableColumnVisibilityChange,
  } = useTableQueryParams({
    order: stringify([{ orderBy: 'name' }]),
  });

  const [usersListState, setUsersListState] = useState<UserModel[]>([]);
  const [isTouched, setIsTouched] = useState<boolean>(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [saveDialog, setSaveDialog] = useState(false);
  const { activePanel, closePanel, setPanel } = usePanelState();
  const { data: usersData, isLoading: usersIsLoading, dataUpdatedAt } = useUserList(apiParams);
  // exportParams deliberately drops `search` (see useTableQueryParams), which would make the sheet
  // a superset of the result list the user is looking at once this screen gains a search bar.
  const usersExportParams = useMemo(
    () => ({ ...exportParams, search: params.search }),
    [exportParams, params.search]
  );
  const {
    isLoading: isExportLoading,
    isRefetching: isExportRefetching,
    refetch: exportUsers,
  } = useExportUsers(usersExportParams);

  // A disabled query never publishes error state to the observer, and there is no global
  // QueryCache.onError - without this a failed export is completely silent to the user.
  const onExportClickHandler = useCallback(async () => {
    const { isError } = await exportUsers();

    if (isError) {
      showNotification({
        content: t('COMMON.OPERATION_UNSUCCESSFUL'),
        type: NotificationType.ERROR,
      });
    }
  }, [exportUsers]);
  const { data: permissionGroupsData } = usePermissionGroupListSearch();
  const { mutateAsync: editUsers } = useEditUser();
  const { mutateAsync: deleteUser, isPending: isDeleting } = useDeleteUser();

  const {
    isOpen: isDeleteOpen,
    openDialog: openDelete,
    closeDialog: closeDelete,
    selected: selectedDelete,
  } = useDialogState<UserModel>();

  const visible = useCanEdit('OPEM_USERS');

  // The search endpoint already returns only active groups, but stay defensive.
  const activePermissionGroups = useMemo(
    () => (permissionGroupsData ?? []).filter((group) => group.isActive),
    [permissionGroupsData]
  );

  useEffect(() => {
    const defaultState = usersData?.data;
    if (!defaultState || defaultState.length !== usersListState.length) {
      return;
    }

    setIsTouched((prev) => {
      const next = isChangesHasOccured(defaultState, usersListState, 'id');
      return prev === next ? prev : next;
    });
  }, [usersListState, usersData?.data]);

  useEffect(() => {
    if (usersIsLoading || !usersData?.data) {
      return;
    }

    const nextData = usersData.data;
    setUsersListState((prev) => (prev === nextData ? prev : nextData));
  }, [usersData?.data, usersIsLoading]);

  const getCheckboxFieldClickHandler =
    <T extends TRecord>(
      listState: UserModel[] | undefined,
      item: T,
      _: ColumnProps<T>,
      originalList: UserModel[]
    ) =>
    (prevState: UserModel[]) => {
      if (!visible) {
        return prevState;
      }

      const clonedState = cloneDeep(prevState);
      const selected = listState?.find((listItem) => listItem.id === item.id);
      const originalTimeStamp =
        selected?.deletedAt === null
          ? originalList.find((listItem) => listItem.id === item.id)?.deletedAt
          : null;

      const newTimestamp = selected?.deletedAt === null ? DateTime.now().toISO() : null;

      const updatedRow = {
        ...selected,
        deletedAt: originalTimeStamp || newTimestamp,
      };
      const filteredRow = clonedState.filter((stateItem) => stateItem.id !== selected?.id);

      return [...filteredRow, updatedRow];
    };

  const getCheckboxFieldHandler = <T extends TRecord>(
    listState: UserModel[] | undefined,
    item: T,
    column: ColumnProps<T>
  ) =>
    column.field &&
    get(
      listState?.find((stateItem) => stateItem.id === item.id),
      column.field,
      0
    )
      ? 0
      : 1;

  const handlePermissionGroupsChange = useCallback(
    (targetUser: UserModel, newGroups: PermissionGroupModel[]) => {
      if (!visible) {
        return;
      }

      setUsersListState((prevState) => {
        const clonedState = cloneDeep(prevState);
        const currentRow = clonedState.find((stateItem) => stateItem.id === targetUser.id);
        const updatedRow = { ...(currentRow || targetUser), permissionGroups: newGroups };
        const filteredState = clonedState.filter((stateItem) => stateItem.id !== targetUser.id);

        return [...filteredState, updatedRow];
      });
      setIsTouched(true);
    },
    [visible]
  );

  const columns: ColumnProps<UserModel>[] = [
    {
      key: 'name',
      header: <Trans i18nKey="USER.NAME" />,
      accessor: (userData) => userData.name,
      field: 'name',
      sortable: true,
      noWrap: true,
    },
    {
      key: 'email',
      header: <Trans i18nKey="USER.EMAIL_ADDRESS" />,
      accessor: (userData) => userData.email,
      field: 'email',
      sortable: true,
      noWrap: true,
    },
    {
      key: 'deletedAt',
      header: <Trans i18nKey="TABLE.ACTIVE" />,
      accessor: (userData) => userData.deletedAt,
      field: 'deletedAt',
      align: 'center',
      sortable: false,
      noWrap: true,
      checkboxClickHandler: getCheckboxFieldClickHandler,
      checkboxFieldHandler: getCheckboxFieldHandler,
      checkboxCell: true,
    },
    {
      key: 'permissionGroups',
      header: <Trans i18nKey="KOZOS_OPEM_FELHASZNALOK.PERMISSION_GROUPS" />,
      accessor: (userData) => {
        const currentRow = usersListState.find((stateItem) => stateItem.id === userData.id) || userData;
        return (
          <PermissionGroupsCell
            userData={currentRow}
            visible={visible}
            activeGroups={activePermissionGroups}
            onChange={(groups) => handlePermissionGroupsChange(currentRow, groups)}
          />
        );
      },
      noWrap: false,
    },
  ];

  const sendOpemUsersList = useCallback(async () => {
    const difference = differenceWith(usersListState, usersData?.data || [], isEqual);
    const updatedData = difference.map((diff) => omit(diff, ['name', 'password']));

    await editUsers({ data: updatedData });

    window.location.reload();
  }, [usersListState, usersData?.data, editUsers]);

  const headerActions = useMemo<PageHeaderAction[]>(() => {
    const onSaveClickHandler = () => {
      setSaveDialog(true);
    };

    const onCancelClickHandler = () => {
      setConfirmDialog(true);
    };

    return [
      {
        name: 'save',
        label: 'COMMON.SAVE',
        icon: <SaveIcon fill="currentColor" />,
        onClick: onSaveClickHandler,
        isBulkAction: !isTouched,
        visible,
      },
      {
        name: 'cancel',
        label: 'COMMON.DISCARD_CHANGES',
        icon: <HighlightOffIcon fill="currentColor" />,
        onClick: onCancelClickHandler,
        isBulkAction: !isTouched,
        visible,
      },
      // No disabledIf here: this screen has no filter panel, so exportParams.filter is always
      // empty - gating on it (as the other export screens do) would disable the button for good.
      {
        name: 'export',
        label: 'COMMON.EXPORT',
        icon: <Export fill="currentColor" />,
        onClick: onExportClickHandler,
        isLoading: isExportLoading || isExportRefetching,
      },
    ];
  }, [isTouched, visible, isExportLoading, isExportRefetching, onExportClickHandler]);

  const onSaveDialogHandler = () => {
    sendOpemUsersList();
    setIsTouched(false);
  };

  const onCancelDialogHandler = () => {
    setUsersListState(usersData?.data || []);
    setIsTouched(false);
  };

  const onDeleteUser = async () => {
    if (selectedDelete?.id) {
      await deleteUser(selectedDelete.id);
      closeDelete();
    }
  };

  const actions = visible
    ? [
        {
          name: 'delete',
          label: 'COMMON.DELETE',
          icon: <DeleteIcon fontSize="small" />,
          onClick: (user: UserModel) => openDelete(user),
        },
      ]
    : [];

  return (
    <Page
      title={<Trans i18nKey="KOZOS_OPEM_FELHASZNALOK.TITLE" />}
      inScroll
      activePanel={activePanel}
      closePanel={closePanel}
      setPanel={setPanel}
    >
      <Table
        listState={usersListState}
        setListState={setUsersListState}
        checkBoxTable={true}
        enableCheckbox={false}
        setPanel={setPanel}
        activePanel={activePanel}
        timestamp={dataUpdatedAt}
        list={usersData?.data || []}
        total={usersData?.meta.total}
        loading={usersIsLoading}
        params={params}
        onSort={onSort}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        pageHeaderActions={headerActions}
        actions={actions}
        columns={columns}
        onTableColumnVisibilityChange={onTableColumnVisibilityChange}
        setIsTouched={setIsTouched}
        onLoadQuery={onLoadQuery}
        dataLoadImmediately={true}
      />
      <ConfirmDialog
        confirmTitle={<Trans i18nKey="COMMON.OK" />}
        cancelTitle={<Trans i18nKey="COMMON.CANCEL" />}
        onSubmit={onSaveDialogHandler}
        open={saveDialog}
        onClose={() => setSaveDialog(false)}
      >
        <Trans i18nKey="COMMON.SAVE_QUESTION" />
      </ConfirmDialog>
      <ConfirmDialog
        confirmTitle={<Trans i18nKey="COMMON.OK" />}
        onSubmit={onCancelDialogHandler}
        open={confirmDialog}
        onClose={() => setConfirmDialog(false)}
      >
        <Trans i18nKey="COMMON.EDIT_QUESTION" />
      </ConfirmDialog>
      <DeleteDialog
        open={isDeleteOpen}
        selected={selectedDelete}
        onClose={closeDelete}
        onSubmit={onDeleteUser}
        loading={isDeleting}
        deleteTitleAccessor="name"
      />
    </Page>
  );
};

export default OpemUsers;

const orderState = <U extends TRecord>(state: U[] | undefined, comp: string) =>
  orderBy(
    state?.map((item) => ({
      ...item,
      permissionGroups: orderBy(item?.permissionGroups, [comp]),
    })),
    [comp]
  );

export const isChangesHasOccured = <T extends TRecord>(
  oldState: T[] | undefined,
  newState: T[] | undefined,
  comparator: string
) => !isEqual(orderBy(orderState(oldState, comparator)), orderBy(orderState(newState, comparator)));
